// components/kyc/KycVerificationBanner.tsx
//
// Prominent KYC verification banner for dashboard

import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { Button } from "@/components/ui/button";
import { Shield, Clock, XCircle } from "lucide-react";
import { useState, useMemo } from "react";
import KycRequiredModal from "./KycRequiredModal";
import { authLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import { cn } from "@/lib/utils";

type KycVerificationBannerProps = {
  isApproved: boolean;
  isPending: boolean;
  isDeclined: boolean;
  needsVerification: boolean;
};

export const KycVerificationBanner = ({
  isApproved,
  isPending,
  isDeclined,
  needsVerification,
}: KycVerificationBannerProps) => {
  const { user } = useAuth();
  const [showKycModal, setShowKycModal] = useState(false);
  const labels = useMemo(() => authLabels(getLanguage()), []);

  if (!user || user.type === UserType.Admin || isApproved) {
    return null;
  }

  const getStatusConfig = () => {
    if (isPending) {
      return {
        icon: Clock,
        text: labels.kyc?.status?.inProgress,
        shell:
          "border-amber-500/30 bg-gradient-to-r from-amber-50/90 via-white to-slate-50/80 dark:from-amber-950/40 dark:via-background dark:to-slate-950/30",
        iconWrap:
          "bg-amber-500/15 text-amber-700 dark:text-amber-300",
        badge:
          "bg-amber-500/15 text-amber-800 dark:text-amber-200",
        badgeText: labels.kyc?.banner?.badgePending || "Pågår",
        buttonClass:
          "bg-amber-700 hover:bg-amber-600 text-white",
        buttonText: labels.kyc?.banner?.checkStatus,
      };
    }
    if (isDeclined) {
      return {
        icon: XCircle,
        text: labels.kyc?.status?.declined,
        shell:
          "border-red-500/30 bg-gradient-to-r from-red-50/90 via-white to-slate-50/80 dark:from-red-950/40 dark:via-background dark:to-slate-950/30",
        iconWrap: "bg-red-500/15 text-red-700 dark:text-red-300",
        badge: "bg-red-500/15 text-red-800 dark:text-red-200",
        badgeText: labels.kyc?.banner?.badgeDeclined || "Avvisad",
        buttonClass: "bg-red-700 hover:bg-red-600 text-white",
        buttonText: labels.kyc?.banner?.tryAgain,
      };
    }
    return {
      icon: Shield,
      text: labels.kyc?.banner?.verifyIdentity,
      shell:
        "border-[#D4AF37]/35 bg-gradient-to-r from-[#D4AF37]/10 via-white to-slate-50/80 dark:from-[#D4AF37]/15 dark:via-background dark:to-slate-950/30",
      iconWrap: "bg-[#D4AF37]/15 text-[#B8941F] dark:text-[#D4AF37]",
      badge: "bg-[#D4AF37]/15 text-[#8B7015] dark:text-[#D4AF37]",
      badgeText: labels.kyc?.banner?.badge || "Krävs",
      buttonClass: "bg-[#B8941F] hover:bg-[#A6831A] text-white",
      buttonText: labels.kyc?.dialog?.verifyIdentity,
    };
  };

  const statusConfig = getStatusConfig();
  const Icon = statusConfig.icon;
  const subtitle = needsVerification
    ? labels.kyc?.banner?.requiredFor
    : labels.kyc?.banner?.underReview;

  return (
    <>
      <div
        className={cn(
          "w-full md:w-[85%] md:mx-auto mb-4 rounded-xl border shadow-sm",
          statusConfig.shell,
        )}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div
              className={cn(
                "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                statusConfig.iconWrap,
              )}
            >
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                    statusConfig.badge,
                  )}
                >
                  {statusConfig.badgeText}
                </span>
                <p className="text-sm font-semibold text-foreground">
                  {statusConfig.text}
                </p>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {subtitle}
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowKycModal(true)}
            size="sm"
            className={cn(
              "shrink-0 self-stretch sm:self-center whitespace-nowrap",
              statusConfig.buttonClass,
            )}
          >
            {statusConfig.buttonText}
          </Button>
        </div>
      </div>
      <KycRequiredModal
        isOpen={showKycModal}
        onClose={() => setShowKycModal(false)}
        actionName={labels.kyc?.banner?.verifyIdentity}
      />
    </>
  );
};
